import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Popover,
  Grid,
  Paper,
} from "@mui/material";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import * as XLSX from "xlsx";
import TableChartIcon from "@mui/icons-material/TableChart";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// Register custom table blot for Quill [web:6][web:9]
const BlockEmbed = Quill.import("blots/block/embed");

class TableBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.innerHTML = value;
    node.setAttribute("contenteditable", "false");
    node.style.position = "relative";
    return node;
  }

  static value(node) {
    return node.innerHTML;
  }
}

TableBlot.blotName = "customTable";
TableBlot.tagName = "div";
TableBlot.className = "custom-table-wrapper";

Quill.register(TableBlot);

const EditDescription = ({ domain, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    domainName: "",
    domainDescription: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [tableAnchor, setTableAnchor] = useState(null);
  const [tableSize, setTableSize] = useState({ rows: 6, cols: 3 });
  const [uploadedFile, setUploadedFile] = useState(null);
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);

  const API_BASE = "https://erpapi.eduskillsfoundation.org/aiservices";

  const customStyles = `
    .custom-table-wrapper {
      margin: 15px 0;
      width: 100%;
      position: relative;
    }

    .custom-table {
      border-collapse: collapse;
      width: 100%;
      border: 2px solid #1976d2;
      background: white;
    }

    .custom-table thead {
      background-color: #1976d2;
    }

    .custom-table th {
      background-color: #1976d2;
      color: white;
      border: 1px solid #1976d2;
      padding: 12px;
      font-weight: bold;
      text-align: left;
    }

    .custom-table td {
      border: 1px solid #1976d2;
      padding: 12px;
      text-align: left;
      background-color: white;
    }

    .custom-table tbody tr:nth-child(even) td {
      background-color: #f5f5f5;
    }

    .custom-table tbody tr:hover td {
      background-color: #e3f2fd;
    }

    .custom-table td[contenteditable="true"]:focus,
    .custom-table th[contenteditable="true"]:focus {
      outline: 2px solid #1976d2;
      outline-offset: -2px;
      background-color: #e3f2fd;
    }

    .table-delete-btn {
      position: absolute;
      top: 5px;
      right: 5px;
      background: #f44336;
      color: white;
      border: none;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      font-size: 16px;
      line-height: 1;
    }

    .custom-table-wrapper:hover .table-delete-btn {
      display: flex;
    }
  `;

  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = customStyles;
    styleTag.id = "custom-table-styles";

    const existingStyle = document.getElementById("custom-table-styles");
    if (existingStyle) {
      existingStyle.remove();
    }

    document.head.appendChild(styleTag);

    return () => {
      const styleToRemove = document.getElementById("custom-table-styles");
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (domain && domain.domain_description) {
      setFormData({
        domainName: domain.domain_name || "",
        domainDescription: domain.domain_description || "",
      });
    }
  }, [domain]);

  // Get Quill editor instance [web:3][web:8]
  const getQuillEditor = () => {
    if (quillRef.current) {
      return quillRef.current.getEditor();
    }
    return null;
  };

  // Insert table at cursor position [web:1][web:3]
  const insertTableAtCursor = (tableHTML) => {
    const editor = getQuillEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    const position = range ? range.index : editor.getLength();

    // Insert the table as a custom embed [web:3][web:9]
    editor.insertEmbed(position, "customTable", tableHTML, "user");

    // Move cursor after the table
    editor.setSelection(position + 1, 0);

    // Focus the editor
    editor.focus();
  };

  // Handle Excel file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedFile(file.name);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary" });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          setError("The Excel file is empty");
          return;
        }

        createTableFromExcelData(jsonData);
        setError("");
        setUploadedFile(null);
      } catch (err) {
        console.error("Error parsing Excel file:", err);
        setError(
          "Failed to parse Excel file. Please ensure it's a valid .xlsx or .xls file."
        );
        setUploadedFile(null);
      }
    };

    reader.onerror = () => {
      setError("Error reading file");
      setUploadedFile(null);
    };

    reader.readAsBinaryString(file);
  };

  // Create table HTML from Excel data
  const createTableFromExcelData = (data) => {
    if (data.length < 2) {
      setError("Excel file must have at least a header row and one data row");
      return;
    }

    const headers = data[0];
    const rows = data
      .slice(1)
      .filter(
        (row) =>
          row &&
          row.some((cell) => cell !== undefined && cell !== null && cell !== "")
      );
    const cols = headers.length;

    let tableHTML = `<table class="custom-table" border="1" cellpadding="12" cellspacing="0">
  <thead>
    <tr>`;

    headers.forEach((header) => {
      const headerText =
        header !== undefined && header !== null ? String(header) : "Header";
      tableHTML += `
      <th contenteditable="true">${headerText}</th>`;
    });

    tableHTML += `
    </tr>
  </thead>
  <tbody>`;

    rows.forEach((row) => {
      tableHTML += `
    <tr>`;
      for (let j = 0; j < cols; j++) {
        const cellValue =
          row[j] !== undefined && row[j] !== null ? String(row[j]) : "";
        tableHTML += `
      <td contenteditable="true">${cellValue}</td>`;
      }
      tableHTML += `
    </tr>`;
    });

    tableHTML += `
  </tbody>
</table>`;

    // Insert at cursor position [web:1][web:3]
    insertTableAtCursor(tableHTML);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Create manual table [web:7]
  const createTable = (rows, cols) => {
    let tableHTML = `<table class="custom-table" border="1" cellpadding="12" cellspacing="0">
  <thead>
    <tr>`;

    const headers = ["Module", "Topic Covered", "Description"];
    for (let j = 0; j < cols; j++) {
      const headerText = j < headers.length ? headers[j] : `Header ${j + 1}`;
      tableHTML += `
      <th contenteditable="true">${headerText}</th>`;
    }

    tableHTML += `
    </tr>
  </thead>
  <tbody>`;

    for (let i = 1; i < rows; i++) {
      tableHTML += `
    <tr>`;
      for (let j = 0; j < cols; j++) {
        let cellContent = "";
        if (j === 0) {
          cellContent = `Module ${i}`;
        } else if (j === 1) {
          cellContent = `Topic ${i}`;
        } else {
          cellContent = `Description for module ${i}`;
        }
        tableHTML += `
      <td contenteditable="true">${cellContent}</td>`;
      }
      tableHTML += `
    </tr>`;
    }

    tableHTML += `
  </tbody>
</table>`;

    // Insert at cursor position [web:1][web:3]
    insertTableAtCursor(tableHTML);
    setTableAnchor(null);
  };

  const handleTableIconClick = (event) => {
    setTableAnchor(event.currentTarget);
  };

  const handleTableClose = () => {
    setTableAnchor(null);
  };

  const handleTableSizeSelect = (rows, cols) => {
    createTable(rows, cols);
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link", "image"],
      ["blockquote", "code-block"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
    "color",
    "background",
    "align",
    "code-block",
    "customTable", // Add custom table format [web:9]
  ];

  const getAuthToken = () => {
    const token = localStorage.getItem("accessToken");
    return token;
  };

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = () => {
    const token = getAuthToken();
    if (!token) {
      setShowAuthDialog(true);
      setError("You need to be logged in to edit domain descriptions.");
    }
  };

  const updateDomainDescription = async (domain_id, domain_description) => {
    const token = getAuthToken();

    if (!token) {
      throw new Error("Authentication required. Please log in again.");
    }

    try {
      const response = await fetch(`${API_BASE}/updateDomainDescription`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          domain_id,
          domain_description,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication failed. Please log in again.");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  };

  const handleRedirectToLogin = () => {
    window.location.href = "/login";
  };

  const handleCloseAuthDialog = () => {
    setShowAuthDialog(false);
    onCancel();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDescriptionChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      domainDescription: content,
    }));
  };

  const handleSave = async () => {
    const token = getAuthToken();
    if (!token) {
      setError("Your session has expired. Please log in again.");
      setShowAuthDialog(true);
      return;
    }

    if (!formData.domainName.trim()) {
      setError("Domain name is required");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const data = await updateDomainDescription(
        domain.domain_id,
        formData.domainDescription
      );

      if (
        data.status === "success" ||
        data.message?.includes("success") ||
        !data.message?.includes("Error")
      ) {
        onSave({
          ...domain,
          domain_name: formData.domainName,
          domain_description: formData.domainDescription,
        });
      } else {
        throw new Error(data.message || "Failed to update domain description");
      }
    } catch (err) {
      console.error("Error saving domain data:", err);
      const errorMessage =
        err.message || "Error saving changes. Please try again.";
      setError(errorMessage);

      if (
        err.message.includes("Authentication") ||
        err.message.includes("401") ||
        err.message.includes("auth")
      ) {
        localStorage.removeItem("accessToken");
        setShowAuthDialog(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const AuthDialog = () => (
    <Dialog open={showAuthDialog} onClose={handleCloseAuthDialog}>
      <DialogTitle>Authentication Required</DialogTitle>
      <DialogContent>
        <Typography>
          You need to be logged in to edit domain descriptions.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          Token found: {getAuthToken() ? "Yes" : "No"}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseAuthDialog}>Cancel</Button>
        <Button
          onClick={handleRedirectToLogin}
          variant="contained"
          color="primary"
        >
          Go to Login
        </Button>
      </DialogActions>
    </Dialog>
  );

  const TableSizeSelector = () => (
    <Popover
      open={Boolean(tableAnchor)}
      anchorEl={tableAnchor}
      onClose={handleTableClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <Box sx={{ p: 2, minWidth: 250 }}>
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          Select Table Size
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            label="Rows"
            type="number"
            size="small"
            value={tableSize.rows}
            onChange={(e) =>
              setTableSize({
                ...tableSize,
                rows: parseInt(e.target.value) || 2,
              })
            }
            inputProps={{ min: 2, max: 15 }}
            sx={{ width: 100 }}
          />
          <TextField
            label="Columns"
            type="number"
            size="small"
            value={tableSize.cols}
            onChange={(e) =>
              setTableSize({
                ...tableSize,
                cols: parseInt(e.target.value) || 2,
              })
            }
            inputProps={{ min: 2, max: 10 }}
            sx={{ width: 100 }}
          />
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          Quick Templates:
        </Typography>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Button
              size="small"
              variant="contained"
              fullWidth
              onClick={() => handleTableSizeSelect(6, 3)}
            >
              Module (6×3)
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={() => handleTableSizeSelect(5, 5)}
            >
              5×5
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={() => handleTableSizeSelect(3, 3)}
            >
              3×3
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={() => handleTableSizeSelect(10, 3)}
            >
              10×3
            </Button>
          </Grid>
        </Grid>
        <Button
          variant="contained"
          size="small"
          fullWidth
          onClick={() => handleTableSizeSelect(tableSize.rows, tableSize.cols)}
        >
          Insert Custom Table
        </Button>
      </Box>
    </Popover>
  );

  return (
    <Box sx={{ py: 2 }}>
      <AuthDialog />
      <TableSizeSelector />

      {error && !showAuthDialog && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {uploadedFile && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Processing file: {uploadedFile}
        </Alert>
      )}

      {!showAuthDialog ? (
        <>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Domain Name"
              name="domainName"
              value={formData.domainName}
              onChange={handleInputChange}
              disabled={loading}
              margin="normal"
              required
            />

            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Domain Description</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Upload Excel File">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CloudUploadIcon />}
                        onClick={handleUploadClick}
                        disabled={loading}
                        sx={{
                          bgcolor: "success.main",
                          "&:hover": {
                            bgcolor: "success.dark",
                          },
                        }}
                      >
                        Upload Excel
                      </Button>
                    </Tooltip>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                    />

                    <Tooltip title="Insert Manual Table">
                      <IconButton
                        color="primary"
                        onClick={handleTableIconClick}
                        size="small"
                        disabled={loading}
                        sx={{
                          bgcolor: "primary.main",
                          color: "white",
                          "&:hover": {
                            bgcolor: "primary.dark",
                          },
                        }}
                      >
                        <TableChartIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 2 }}
                >
                  Click where you want to insert a table, then upload an Excel
                  file or click the table icon. Tables will appear at your
                  cursor position. All table cells are editable.
                </Typography>
                <ReactQuill
                  ref={quillRef}
                  value={formData.domainDescription}
                  onChange={handleDescriptionChange}
                  modules={modules}
                  formats={formats}
                  theme="snow"
                  style={{ height: "400px", marginBottom: "70px" }}
                  readOnly={loading}
                />
              </CardContent>
            </Card>
          </Box>

          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}
          >
            <Button
              onClick={onCancel}
              disabled={loading}
              variant="outlined"
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading}
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>

          <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Debug Info: Token: {getAuthToken() ? "Present" : "Missing"} |
              Domain ID: {domain?.domain_id}
            </Typography>
          </Box>
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Authentication Required
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please log in to edit domain descriptions.
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
            Access Token:{" "}
            {getAuthToken() ? "Found" : "Not found in localStorage"}
          </Typography>
          <Button
            onClick={handleRedirectToLogin}
            variant="contained"
            color="primary"
            sx={{ mr: 2 }}
          >
            Go to Login
          </Button>
          <Button onClick={handleCloseAuthDialog} variant="outlined">
            Cancel
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default EditDescription;
